/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – ConfigPanel.tsx FINAL
// Adăugat: Bulk save senzori, persistență notificări localStorage
// ============================================================

import React, { useState, useEffect } from 'react';
import {
  InputNumber, Select, Switch, Form, Button,
  Alert, message, TimePicker,
} from 'antd';
import {
  SettingOutlined, SaveOutlined, ReloadOutlined,
  BellOutlined, MailOutlined, WifiOutlined,
  DatabaseOutlined, EditOutlined, CheckOutlined,
  WarningOutlined, InfoCircleOutlined, ClockCircleOutlined,
  EnvironmentOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import { mockSensors } from '../../data/mockData';
import { SensorStatus } from '../../types';
import { useSimulatorData } from '../../context/SimulatorContext';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;

const LS_CONFIG_KEY = 'simadca_config_v3';

// ─── Tipuri ───────────────────────────────────────────────

interface SensorCfg {
  id: string; name: string; parameter: string;
  lod: number; maxAllowed: number; unit: string;
  status: SensorStatus; type: string;
  alertEnabled: boolean; calibDays: number;
}
interface NotifCfg {
  emailEnabled: boolean; smsEnabled: boolean;
  email: string; phone: string;
  levels: string[]; quietFrom: string; quietTo: string;
  digest: 'realtime' | 'hourly' | 'daily';
}
interface SysCfg {
  refreshSec: number; retentionDays: number;
  autoReport: boolean; reportTime: string;
  mapZoom: number; language: string; maintenance: boolean;
}

const SC: Record<SensorStatus, string> = {
  online: '#10b981', warning: '#f59e0b', offline: '#ef4444', calibrating: '#6366f1',
};
const SL: Record<SensorStatus, string> = {
  online: 'Online', warning: 'Avertisment', offline: 'Offline', calibrating: 'Calibrare',
};

const initSensors = (): SensorCfg[] => mockSensors.map(s => ({
  id: s.id, name: s.name, parameter: s.parameter,
  lod: s.lod, maxAllowed: s.maxAllowed, unit: s.unit,
  status: s.status, type: s.type,
  alertEnabled: s.status !== 'offline', calibDays: 30,
}));

const defaultNotif: NotifCfg = {
  emailEnabled: true, smsEnabled: false,
  email: 'admin@simadca.ro', phone: '+40 700 000 000',
  levels: ['critical', 'high'], quietFrom: '22:00', quietTo: '07:00',
  digest: 'realtime',
};
const defaultSys: SysCfg = {
  refreshSec: 30, retentionDays: 365,
  autoReport: true, reportTime: '06:00',
  mapZoom: 6, language: 'ro', maintenance: false,
};

// ─── Load/save config ─────────────────────────────────────

const loadConfig = () => {
  try {
    const s = localStorage.getItem(LS_CONFIG_KEY);
    if (!s) return { notif: defaultNotif, sys: defaultSys };
    return JSON.parse(s);
  } catch { return { notif: defaultNotif, sys: defaultSys }; }
};
const saveConfig = (notif: NotifCfg, sys: SysCfg) => {
  try { localStorage.setItem(LS_CONFIG_KEY, JSON.stringify({ notif, sys })); } catch {}
};

// ─── Wrapper secțiune ─────────────────────────────────────

const Section: React.FC<{ icon: React.ReactNode; title: string; sub?: string; color?: string; children: React.ReactNode; action?: React.ReactNode }> =
  ({ icon, title, sub, color = '#6366f1', children, action }) => (
  <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', marginBottom: 16 }}>
    <div style={{ padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: `${color}15`, fontSize: 16, color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{title}</div>
          {sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}
        </div>
      </div>
      {action}
    </div>
    <div style={{ padding: '16px 20px' }}>{children}</div>
  </div>
);

const FRow: React.FC<{ label: string; sub?: string; children: React.ReactNode }> = ({ label, sub, children }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
    <div>
      <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
    <div style={{ flexShrink: 0, marginLeft: 16 }}>{children}</div>
  </div>
);

// ═══════════════════════════════════════════════
// TAB 1 – SENZORI cu Bulk Save
// ═══════════════════════════════════════════════

const SensorsTab: React.FC = () => {
  const { updateLOD }    = useSimulatorData();
  const { logActivity }  = useAuth();
  const [cfgs, setCfgs]  = useState<SensorCfg[]>(initSensors());
  const [editing, setEditing] = useState<string | null>(null);
  const [draft,   setDraft]   = useState<Partial<SensorCfg>>({});
  const [dirty,   setDirty]   = useState<Set<string>>(new Set());

  const startEdit = (c: SensorCfg) => {
    setEditing(c.id);
    setDraft({ lod: c.lod, maxAllowed: c.maxAllowed, alertEnabled: c.alertEnabled, calibDays: c.calibDays });
  };

  const markDirty = (id: string, field: string, val: any) => {
    setDraft(p => ({ ...p, [field]: val }));
    setDirty(p => new Set(p).add(id));
  };

  const saveOne = (id: string) => {
    setCfgs(p => p.map(c => c.id === id ? { ...c, ...draft } : c));
    if (draft.lod !== undefined) updateLOD(id, draft.lod);
    setEditing(null);
    setDirty(p => { const s = new Set(p); s.delete(id); return s; });
    message.success('Configurație salvată.');
    logActivity(`Config senzor salvat: ${id}`, 'Configurare');
  };

  const saveAll = () => {
    // Salvează toate modificările draft pentru senzorul în editare curent
    if (editing) {
      setCfgs(p => p.map(c => c.id === editing ? { ...c, ...draft } : c));
      if (draft.lod !== undefined) updateLOD(editing, draft.lod);
    }
    cfgs.forEach(c => { if (c.lod !== mockSensors.find(s => s.id === c.id)?.lod) updateLOD(c.id, c.lod); });
    setEditing(null); setDirty(new Set());
    message.success(`${cfgs.length} senzori salvați cu succes!`);
    logActivity('Bulk save configurație senzori', 'Configurare');
  };

  const resetOne = (id: string) => {
    const orig = mockSensors.find(s => s.id === id);
    if (!orig) return;
    setCfgs(p => p.map(c => c.id === id ? { ...c, lod: orig.lod, maxAllowed: orig.maxAllowed, alertEnabled: true, calibDays: 30 } : c));
    message.info('Resetat la valorile implicite.');
  };

  return (
    <>
      <Alert type="info" showIcon icon={<InfoCircleOutlined />} style={{ marginBottom: 16, borderRadius: 10 }}
        message="Modificările LOD afectează imediat calculul alertelor și predicțiilor AI în timp real." />

      {/* Bulk save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={saveAll} style={{ padding: '8px 18px', borderRadius: 9, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          <SaveOutlined /> Salvează toți ({cfgs.length})
        </button>
      </div>

      {/* Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1.2fr 0.8fr 0.8fr 1.2fr', gap: 8, padding: '8px 14px', background: '#f8fafc', borderRadius: '10px 10px 0 0', border: '1px solid #f1f5f9', fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em' }}>
        {['SENZOR', 'STATUS', 'LOD', 'MAX LEGAL', 'CALIBR.', 'ALERTE', 'ACȚIUNI'].map(h => <div key={h}>{h}</div>)}
      </div>

      <div style={{ border: '1px solid #f1f5f9', borderTop: 'none', borderRadius: '0 0 10px 10px', overflow: 'hidden' }}>
        {cfgs.map((c, idx) => {
          const isEdit = editing === c.id;
          return (
            <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 1.2fr 0.8fr 0.8fr 1.2fr', gap: 8, padding: '10px 14px', alignItems: 'center', background: isEdit ? '#fafbff' : idx % 2 === 0 ? '#fff' : '#fafafa', borderBottom: idx < cfgs.length - 1 ? '1px solid #f8fafc' : 'none' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{c.name}</div>
                <div style={{ fontSize: 10, color: '#94a3b8' }}>{c.parameter} · {c.unit}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: SC[c.status], display: 'inline-block' }} />
                <span style={{ fontSize: 11, color: '#64748b' }}>{SL[c.status]}</span>
              </div>
              {isEdit ? (
                <InputNumber value={draft.lod} size="small" step={0.01} min={0} style={{ width: '100%' }} onChange={v => markDirty(c.id, 'lod', v ?? c.lod)} />
              ) : (
                <span style={{ fontSize: 12, fontWeight: 600, color: c.lod !== mockSensors.find(s => s.id === c.id)?.lod ? '#f59e0b' : '#0f172a' }}>{c.lod} {c.unit}</span>
              )}
              {isEdit ? (
                <InputNumber value={draft.maxAllowed} size="small" step={0.1} min={0} style={{ width: '100%' }} onChange={v => markDirty(c.id, 'maxAllowed', v ?? c.maxAllowed)} />
              ) : (
                <span style={{ fontSize: 12, color: '#64748b' }}>{c.maxAllowed} {c.unit}</span>
              )}
              {isEdit ? (
                <InputNumber value={draft.calibDays} size="small" min={1} max={365} style={{ width: '100%' }} onChange={v => markDirty(c.id, 'calibDays', v ?? c.calibDays)} />
              ) : (
                <span style={{ fontSize: 12, color: '#64748b' }}>{c.calibDays}z</span>
              )}
              <Switch size="small" checked={isEdit ? (draft.alertEnabled ?? c.alertEnabled) : c.alertEnabled}
                onChange={v => { if (isEdit) markDirty(c.id, 'alertEnabled', v); else setCfgs(p => p.map(x => x.id === c.id ? { ...x, alertEnabled: v } : x)); }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                {isEdit ? (
                  <>
                    <button onClick={() => saveOne(c.id)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>Salvează</button>
                    <button onClick={() => setEditing(null)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 10, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(c)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <EditOutlined />
                    </button>
                    <button onClick={() => resetOne(c.id)} style={{ padding: '4px 8px', borderRadius: 6, fontSize: 10, border: '1px solid #f1f5f9', background: '#f8fafc', color: '#94a3b8', cursor: 'pointer', fontFamily: 'inherit' }}>
                      <ReloadOutlined />
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// ═══════════════════════════════════════════════
// TAB 2 – NOTIFICĂRI cu persistență
// ═══════════════════════════════════════════════

const NotifTab: React.FC = () => {
  const { logActivity } = useAuth();
  const saved = loadConfig();
  const [cfg, setCfg]   = useState<NotifCfg>(saved.notif ?? defaultNotif);

  const save = () => {
    saveConfig(cfg, loadConfig().sys ?? defaultSys);
    message.success('Setări notificări salvate!');
    logActivity('Setări notificări salvate', 'Configurare');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Section icon={<MailOutlined />} title="Email & SMS" color="#6366f1">
        <FRow label="Notificări email" sub="Trimite alerte pe email">
          <Switch checked={cfg.emailEnabled} onChange={v => setCfg(p => ({ ...p, emailEnabled: v }))} />
        </FRow>
        <FRow label="Adresă email" sub="Destinatar notificări">
          <input value={cfg.email} onChange={e => setCfg(p => ({ ...p, email: e.target.value }))}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, width: 220, fontFamily: 'inherit' }} />
        </FRow>
        <FRow label="Notificări SMS" sub="Trimite SMS pentru alerte critice">
          <Switch checked={cfg.smsEnabled} onChange={v => setCfg(p => ({ ...p, smsEnabled: v }))} />
        </FRow>
        <FRow label="Număr telefon" sub="Destinatar SMS">
          <input value={cfg.phone} onChange={e => setCfg(p => ({ ...p, phone: e.target.value }))}
            style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, width: 180, fontFamily: 'inherit' }} />
        </FRow>
      </Section>

      <Section icon={<BellOutlined />} title="Praguri și program" color="#f59e0b">
        <FRow label="Severități notificate" sub="Primești notificări pentru nivelele selectate">
          <Select mode="multiple" value={cfg.levels} onChange={v => setCfg(p => ({ ...p, levels: v }))} style={{ width: 260 }} size="small">
            {[['critical','Critic','#ef4444'], ['high','Ridicat','#f97316'], ['medium','Mediu','#f59e0b'], ['low','Scăzut','#10b981']].map(([v, l, c]) => (
              <Option key={v} value={v}><span style={{ color: c as string, fontWeight: 600 }}>{l}</span></Option>
            ))}
          </Select>
        </FRow>
        <FRow label="Orar silențios" sub="Fără notificări în intervalul selectat">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="time" value={cfg.quietFrom} onChange={e => setCfg(p => ({ ...p, quietFrom: e.target.value }))}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }} />
            <span style={{ color: '#94a3b8' }}>–</span>
            <input type="time" value={cfg.quietTo} onChange={e => setCfg(p => ({ ...p, quietTo: e.target.value }))}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }} />
          </div>
        </FRow>
        <FRow label="Frecvență notificări" sub="Imediat sau grupate">
          <Select value={cfg.digest} onChange={v => setCfg(p => ({ ...p, digest: v }))} size="small" style={{ width: 160 }}>
            <Option value="realtime">Timp real</Option>
            <Option value="hourly">Orar (grupat)</Option>
            <Option value="daily">Zilnic (digest)</Option>
          </Select>
        </FRow>
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={save} style={{ padding: '9px 20px', borderRadius: 9, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          <SaveOutlined /> Salvează setările
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// TAB 3 – SISTEM
// ═══════════════════════════════════════════════

const SystemTab: React.FC = () => {
  const { updateInterval } = useSimulatorData();
  const { logActivity }    = useAuth();
  const saved = loadConfig();
  const [cfg, setCfg]  = useState<SysCfg>(saved.sys ?? defaultSys);
  const [lang, setLang] = useState(saved.sys?.language ?? 'ro');

  const save = () => {
    saveConfig(loadConfig().notif ?? defaultNotif, { ...cfg, language: lang });
    updateInterval(cfg.refreshSec);
    message.success('Setări sistem salvate și aplicate!');
    logActivity('Setări sistem salvate', 'Configurare');
  };

  const handleBackup = () => {
    const data = {
      timestamp: new Date().toISOString(),
      config:    loadConfig(),
      users:     (() => { try { return JSON.parse(localStorage.getItem('simadca_users_v3') ?? '[]'); } catch { return []; } })(),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a'); a.href = url;
    a.download = `simadca_backup_${new Date().toLocaleDateString('ro-RO').replace(/\./g, '_')}.json`; a.click();
    URL.revokeObjectURL(url);
    message.success('Backup descărcat!');
    logActivity('Backup manual efectuat', 'Configurare');
  };

  const handleClearCache = () => {
    const keysToKeep = ['simadca_user_v3', 'simadca_users_v3'];
    const toDelete   = Object.keys(localStorage).filter(k => k.startsWith('simadca_') && !keysToKeep.includes(k));
    toDelete.forEach(k => localStorage.removeItem(k));
    message.success(`Cache șters! (${toDelete.length} intrări eliminate)`);
    logActivity('Cache șters', 'Configurare');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Section icon={<WifiOutlined />} title="Date și performanță" color="#6366f1">
        <FRow label="Interval refresh date" sub="Frecvența actualizării senzorilor">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InputNumber value={cfg.refreshSec} min={5} max={300} style={{ width: 80 }} size="small"
              onChange={v => setCfg(p => ({ ...p, refreshSec: v ?? 30 }))} />
            <span style={{ fontSize: 12, color: '#94a3b8' }}>secunde</span>
          </div>
        </FRow>
        <FRow label="Retenție date" sub="Perioada de păstrare a istoricului">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InputNumber value={cfg.retentionDays} min={30} max={1825} style={{ width: 80 }} size="small"
              onChange={v => setCfg(p => ({ ...p, retentionDays: v ?? 365 }))} />
            <span style={{ fontSize: 12, color: '#94a3b8' }}>zile</span>
          </div>
        </FRow>
        <FRow label="Zoom harta implicit" sub="Nivel de zoom la pornire">
          <InputNumber value={cfg.mapZoom} min={4} max={16} style={{ width: 80 }} size="small"
            onChange={v => setCfg(p => ({ ...p, mapZoom: v ?? 6 }))} />
        </FRow>
      </Section>

      <Section icon={<ClockCircleOutlined />} title="Rapoarte automate" color="#10b981">
        <FRow label="Raport zilnic automat" sub="Generează raport la ora setată">
          <Switch checked={cfg.autoReport} onChange={v => setCfg(p => ({ ...p, autoReport: v }))} />
        </FRow>
        <FRow label="Ora raport zilnic" sub="HH:MM">
          <input type="time" value={cfg.reportTime} onChange={e => setCfg(p => ({ ...p, reportTime: e.target.value }))}
            style={{ padding: '5px 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 12, fontFamily: 'inherit' }} />
        </FRow>
      </Section>

      <Section icon={<SettingOutlined />} title="Preferințe interfață" color="#f59e0b">
        <FRow label="Limbă interfață" sub="RO: Română · EN: Engleză">
          <Select value={lang} onChange={v => { setLang(v); message.info(`Limbă selectată: ${v === 'ro' ? 'Română' : 'English'} — se aplică la salvare.`); }} size="small" style={{ width: 130 }}>
            <Option value="ro">🇷🇴 Română</Option>
            <Option value="en">🇬🇧 English</Option>
          </Select>
        </FRow>
        <FRow label="Mod mentenanță" sub="Restricționează accesul non-admin">
          <Switch checked={cfg.maintenance} onChange={v => setCfg(p => ({ ...p, maintenance: v }))} />
        </FRow>
      </Section>

      <Section icon={<DatabaseOutlined />} title="Date și backup" color="#ef4444">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={handleBackup} style={{ padding: '8px 16px', borderRadius: 9, fontSize: 12, fontWeight: 600, border: '1px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            <DatabaseOutlined /> Backup manual (JSON)
          </button>
          <button onClick={handleClearCache} style={{ padding: '8px 16px', borderRadius: 9, fontSize: 12, fontWeight: 600, border: '1px solid #fecaca', background: '#fff5f5', color: '#ef4444', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
            <ReloadOutlined /> Curăță cache
          </button>
        </div>
        <Alert type="warning" showIcon style={{ marginTop: 12, borderRadius: 8 }} message="Backup-ul include configurațiile și utilizatorii. Datele senzorilor se stochează pe server." />
      </Section>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={save} style={{ padding: '9px 20px', borderRadius: 9, fontSize: 12, fontWeight: 700, border: 'none', background: '#6366f1', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6 }}>
          <SaveOutlined /> Aplică și salvează setările
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════
// PANEL PRINCIPAL
// ═══════════════════════════════════════════════

const TAB_ITEMS = [
  { key: 'sensors', icon: <EnvironmentOutlined />, label: 'Senzori' },
  { key: 'notif',   icon: <BellOutlined />,        label: 'Notificări' },
  { key: 'system',  icon: <DatabaseOutlined />,    label: 'Sistem' },
];

const ConfigPanel: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('sensors');

  const tabs = TAB_ITEMS.filter(t => t.key !== 'system' || user?.role === 'admin');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.4px' }}>Configurare</h2>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>Setări senzori LOD, notificări și sistem · Persistent în localStorage</span>
      </div>

      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9', padding: '0 16px' }}>
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
              padding: '14px 18px', fontSize: 13,
              fontWeight: activeTab === t.key ? 700 : 500,
              color: activeTab === t.key ? '#6366f1' : '#64748b',
              border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: `2px solid ${activeTab === t.key ? '#6366f1' : 'transparent'}`,
              marginBottom: -1, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 7, transition: 'all 0.15s',
            }}>
              <span style={{ color: activeTab === t.key ? '#6366f1' : '#94a3b8', fontSize: 15 }}>{t.icon}</span>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ padding: '20px' }}>
          {activeTab === 'sensors' && <SensorsTab />}
          {activeTab === 'notif'   && <NotifTab />}
          {activeTab === 'system'  && <SystemTab />}
        </div>
      </div>
    </div>
  );
};

export default ConfigPanel;